import React, { useRef, useState } from 'react';
import { Camera, Upload } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { compressImage } from '../../utils/image-compression';

interface ProfilePictureUploadProps {
  currentPictureUrl?: string | null;
  onUpload: (file: File) => Promise<void>;
  isUploading?: boolean;
}

const ProfilePictureUpload: React.FC<ProfilePictureUploadProps> = observer(({
  currentPictureUrl,
  onUpload,
  isUploading = false
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validation du fichier
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError(t('profile.invalidFileType', 'Format non supporté. Utilisez JPEG, PNG ou WebP.'));
      return;
    }

    setError(null);

    // Compresser et uploader le fichier
    try {
      // Compresser l'image avant l'upload
      const compressedFile = await compressImage(file);

      await onUpload(compressedFile);
      // Réinitialiser l'input après upload réussi
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      setError(t('profile.uploadError', 'Erreur lors de l\'upload de l\'image'));
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const displayUrl = currentPictureUrl;
  const initials = ''; // Vous pouvez passer les initiales si besoin

  return (
    <div className="flex flex-col items-center space-y-4">
      {/* Photo de profil */}
      <div className="relative group">
        <div className="w-32 h-32 rounded-full overflow-hidden bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center text-white text-3xl font-bold shadow-lg">
          {displayUrl ? (
            <img
              src={displayUrl}
              alt="Profile"
              className="w-full h-full object-cover"
            />
          ) : (
            <span>{initials || <Camera size={48} />}</span>
          )}
        </div>

        {/* Overlay au hover */}
        <div
          onClick={handleClick}
          className="absolute inset-0 bg-black bg-opacity-50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer flex items-center justify-center"
        >
          {isUploading ? (
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          ) : (
            <Upload className="text-white" size={24} />
          )}
        </div>
      </div>

      {/* Input caché */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileSelect}
        className="hidden"
        disabled={isUploading}
      />

      {/* Bouton visible */}
      <button
        type="button"
        onClick={handleClick}
        disabled={isUploading}
        className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
          isUploading
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-primary hover:bg-primary-dark text-white'
        }`}
      >
        <Camera size={16} className="mr-2" />
        {t('profile.changePhoto', 'Changer la photo')}
      </button>

      {/* Message d'erreur */}
      {error && (
        <p className="text-red-500 text-sm text-center">{error}</p>
      )}

      {/* Info */}
      <p className="text-xs text-gray-500 text-center">
        {t('profile.photoInfo', 'JPEG, PNG ou WebP. Optimisé automatiquement.')}
      </p>
    </div>
  );
});

export default ProfilePictureUpload;
